import {
  CURRENCY_META,
  fmt,
  fmtIn,
  fmtQty,
  meta,
  rateOf,
  type CurrencyCode,
  type NewCustomer,
  type PayMode,
} from '@k2pos/core';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CustomerSheet } from '@/components/ui/CustomerSheet';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { RateSheet } from '@/components/ui/RateSheet';
import { Mono, Text } from '@/components/ui/Text';
import { cx } from '@/lib/cx';
import { IconChevronRight } from '@/components/icons';
import { useBackHandling, useGoBack } from '@/hooks/useNav';
import { useMoneyAction } from '@/hooks/useMoneyAction';
import { cartTotal, usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Cart.module.css';

/**
 * Cart and payment on one screen — the owner asked for them merged, because a
 * counter sale is one motion: check the lines, pick who it's for, take the money.
 */
export function Cart() {
  const navigate = useNavigate();
  const goBack = useGoBack('/sell');
  const cart = usePos((s) => s.cart);
  const customers = usePos((s) => s.customers);
  const customerId = usePos((s) => s.customerId);
  const setCustomer = usePos((s) => s.setCustomer);
  const currency = usePos((s) => s.currency);
  const setCurrency = usePos((s) => s.setCurrency);
  const checkout = usePos((s) => s.checkout);
  const addCustomer = usePos((s) => s.addCustomer);
  const setRate = usePos((s) => s.setRate);
  const shop = usePos((s) => s.shop);

  const [customerSheet, setCustomerSheet] = useState(false);
  const [rateSheet, setRateSheet] = useState(false);

  // Tracked only so the "saving" label lands on the button that was pressed.
  const [pending, setPending] = useState<PayMode | null>(null);
  const total = cartTotal(cart);
  const customer = customers.find((c) => c.id === customerId);

  // The mode travels through `run` rather than through state: a tap must commit
  // with the mode it chose, not with whatever a pending setState has landed.
  const commit = useCallback((mode: PayMode) => checkout(mode), [checkout]);
  const { run, busy, error, dismiss } = useMoneyAction(commit, () =>
    navigate('/done', { replace: true }),
  );

  function pay(mode: PayMode) {
    setPending(mode);
    run(mode);
  }

  const create = useCallback((input: NewCustomer) => addCustomer(input), [addCustomer]);
  const {
    run: runCreate,
    busy: creating,
    error: createError,
    dismiss: dismissCreate,
  } = useMoneyAction(create, (created) => {
    setCustomer(created.id);
    setCustomerSheet(false);
  });

  // Without this, Android back leaves the screen with a sheet still open.
  const openSheets = (customerSheet ? 1 : 0) + (rateSheet ? 1 : 0);
  useBackHandling(false, openSheets, () => {
    setCustomerSheet(false);
    setRateSheet(false);
  });

  return (
    <>
      <AppBar title="ຕະກ້າ" onBack={goBack} />

      <div className={`${styles.body} vscroll`}>
        <ErrorBanner message={error ?? createError} onDismiss={error ? dismiss : dismissCreate} />

        {cart.map((line) => (
          // Composite key: the same product can be in the cart as ຖົງ and ໂຕນ.
          <Card key={`${line.productId}-${line.unit}`} className={styles.line}>
            <div className="grow col">
              <Text weight={700} size={13.5} lines={1}>
                {line.name}
              </Text>
              <Mono size={11} color={colors.textMuted} lines={1}>
                {fmtQty(line.qty)} {line.unit} × {fmt(line.price)}
              </Mono>
            </div>
            <Mono weight={700} size={14} className="selectable">
              {fmt(Math.round(line.price * line.qty))}
            </Mono>
          </Card>
        ))}

        {cart.length === 0 ? (
          <Text size={12} color={colors.textMuted} align="center" className={styles.empty}>
            ຕະກ້າຫວ່າງ · ກັບໄປເລືອກສິນຄ້າກ່ອນ
          </Text>
        ) : null}

        <Text weight={800} size={11} color={colors.textMuted} className={styles.label}>
          ລູກຄ້າ
        </Text>
        {/* One row rather than a strip of everyone: the strip was fine for three
            seeded customers and unusable at the two dozen a real shop carries. */}
        <button type="button" onClick={() => setCustomerSheet(true)} className={styles.customerBtn}>
          <Card className={styles.customerRow}>
            <div className="grow col">
              <Text weight={700} size={13.5} lines={1} align="left">
                {customer?.name ?? 'ລູກຄ້າທົ່ວໄປ'}
              </Text>
              <Mono size={10.5} color={colors.textMuted} align="left">
                {customer
                  ? `${customer.phone || '—'}${customer.balance > 0 ? ` · ຕິດ ${fmt(customer.balance)} ₭` : ''}`
                  : 'ຈ່າຍເລີຍ · ບໍ່ຕິດໜີ້'}
              </Mono>
            </div>
            <Text weight={700} size={12} color={colors.skyDeep}>
              ປ່ຽນ
            </Text>
            <IconChevronRight size={16} className={styles.chevron} />
          </Card>
        </button>

        <Text weight={800} size={11} color={colors.textMuted} className={styles.label}>
          ສະກຸນເງິນ
        </Text>
        <div className={styles.segment} role="radiogroup" aria-label="ສະກຸນເງິນ">
          {CURRENCY_META.map((def) => (
            <CurrencyTab
              key={def.code}
              code={def.code}
              symbol={def.sym}
              selected={def.code === currency}
              onPress={() => setCurrency(def.code)}
            />
          ))}
        </div>

        {currency !== 'LAK' ? (
          <button type="button" onClick={() => setRateSheet(true)} className={styles.rateRow}>
            <Mono size={11.5} color={colors.textMuted} className="grow" align="left">
              1 {meta(currency).sym} = {fmt(rateOf(currency, shop.rates))} ₭
            </Mono>
            <Text weight={700} size={11.5} color={colors.skyDeep}>
              ແກ້ໄຂ
            </Text>
          </button>
        ) : null}

        <Card className={styles.totalCard}>
          <div className={styles.totalRow}>
            <Text weight={800} size={15}>
              ລວມ
            </Text>
            <Mono weight={700} size={22} className="selectable">
              {fmt(total)} ₭
            </Mono>
          </div>
          {currency !== 'LAK' ? (
            // Foreign currency is a capture convenience; the debt book stays in kip.
            <Mono size={11} color={colors.textMuted} align="right">
              ≈ {fmtIn(total, currency, shop.rates)} {meta(currency).sym} · ເກັບເປັນກີບ
            </Mono>
          ) : null}
        </Card>

        <div className={styles.actions}>
          <Button
            label={busy && pending === 'cash' ? 'ກຳລັງບັນທຶກ...' : 'ຈ່າຍເລີຍ'}
            sub="ເງິນສົດ / QR"
            className="grow"
            disabled={cart.length === 0 || busy}
            onPress={() => pay('cash')}
          />
          <Button
            label={busy && pending === 'credit' ? 'ກຳລັງບັນທຶກ...' : 'ຕິດໜີ້ໄວ້'}
            sub={customer ? `ບັນທຶກເປັນໜີ້ ${customer.name}` : 'ຕ້ອງເລືອກລູກຄ້າກ່ອນ'}
            variant="accent"
            className="grow"
            // A debt with no name attached is unrecoverable, so block it here.
            disabled={cart.length === 0 || !customer || busy}
            onPress={() => pay('credit')}
          />
        </div>
      </div>

      <CustomerSheet
        open={customerSheet}
        customers={customers}
        selectedId={customerId}
        busy={creating}
        onSelect={(id) => {
          setCustomer(id);
          setCustomerSheet(false);
        }}
        onCreate={runCreate}
        onClose={() => setCustomerSheet(false)}
      />

      {currency !== 'LAK' ? (
        <RateSheet
          open={rateSheet}
          code={currency}
          current={rateOf(currency, shop.rates)}
          onSave={(rate) => {
            setRate(currency, rate);
            setRateSheet(false);
          }}
          onClose={() => setRateSheet(false)}
        />
      ) : null}
    </>
  );
}

function CurrencyTab({
  code,
  symbol,
  selected,
  onPress,
}: {
  code: CurrencyCode;
  symbol: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={code}
      onClick={onPress}
      className={cx(styles.segItem, selected && styles.segItemOn)}
    >
      <Mono weight={700} size={15} color={selected ? '#FFFFFF' : colors.textMuted}>
        {symbol}
      </Mono>
      <Text size={9} color={selected ? colors.onDarkBright : colors.textFaint}>
        {code}
      </Text>
    </button>
  );
}
